import { useMemo, useState } from 'react'
import {
  FileText, User, Users, Share2, Star, Trash2,
  Plus, Check, MoreVertical, Pencil, X,
} from 'lucide-react'
import {
  Button, Input,
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from '@goodday-clone/ui'
import { cn } from '@/shared/utils/utils'
import { useAuth } from '@/context/AuthContext'
import { usePermissions } from '@/shared/hooks/usePermissions'
import { useDepartments } from '@/shared/hooks/useDepartments'
import type { NoteFolder, NoteSidebarCounts, NotesView } from './notesApi'
import { useNoteFolderMutations } from './notesApi'

const FALLBACK_ROLES = ['Sales', 'Superadmin', 'Development', 'Creative', 'HR', 'Accounts']

const FOLDER_DOT_COLORS: Record<string, string> = {
  green: 'bg-emerald-500', blue: 'bg-blue-500', purple: 'bg-purple-500',
  orange: 'bg-orange-500', pink: 'bg-pink-500', teal: 'bg-teal-500', slate: 'bg-slate-400',
}

interface Props {
  view: NotesView
  folderId: string
  department: string
  counts?: NoteSidebarCounts
  folders: NoteFolder[]
  onNavigate: (view: NotesView) => void
  onSelectFolder: (folderId: string) => void
  onSelectDepartment: (department: string) => void
}

export default function NotepadSidebar({ view, folderId, department, counts, folders, onNavigate, onSelectFolder, onSelectDepartment }: Props) {
  const { user } = useAuth()
  const { hasPermission } = usePermissions()
  const departmentsQuery = useDepartments()
  const folderMutations = useNoteFolderMutations()

  const [folderDialog, setFolderDialog] = useState<{ mode: 'create' | 'rename'; folder?: NoteFolder } | null>(null)
  const [folderName, setFolderName] = useState('')
  const [deletingFolder, setDeletingFolder] = useState<NoteFolder | null>(null)

  const isSuperAdmin = user?.role === 'SUPERADMIN'
  const isAdmin = user?.role === 'ADMIN'
  const isPrivileged = isSuperAdmin || isAdmin
  const roleOptions = useMemo(() => {
    // For SUPERADMIN: only show "Superadmin" department
    if (isSuperAdmin) {
      return ['Superadmin']
    }
    // For ADMIN and regular users: only show their own department
    if (user?.department) {
      return [user.department]
    }
    return []
  }, [isSuperAdmin, isAdmin, user?.department, departmentsQuery.data])

  const navItems: { key: NotesView; label: string; icon: any; count?: number }[] = [
    { key: 'all', label: 'All Notes', icon: FileText, count: counts?.all },
    { key: 'mine', label: 'My Notes', icon: User, count: counts?.mine },
    { key: 'shared', label: 'Shared with Me', icon: Share2, count: counts?.shared },
    { key: 'starred', label: 'Starred', icon: Star, count: counts?.starred },
    { key: 'trash', label: 'Trash', icon: Trash2, count: counts?.trash },
  ]

  const canCreateFolders = hasPermission('NOTES', 'CREATE')

  const openCreateFolder = () => {
    setFolderName('')
    setFolderDialog({ mode: 'create' })
  }
  const openRenameFolder = (folder: NoteFolder) => {
    setFolderName(folder.name)
    setFolderDialog({ mode: 'rename', folder })
  }
  const submitFolderDialog = (e: React.FormEvent) => {
    e.preventDefault()
    const name = folderName.trim()
    if (!name || !folderDialog) return
    if (folderDialog.mode === 'create') {
      folderMutations.create.mutate({ name }, {
        onSuccess: (folder) => {
          setFolderDialog(null)
          onSelectFolder(folder._id)
        },
      })
    } else if (folderDialog.folder) {
      folderMutations.update.mutate({ id: folderDialog.folder._id, data: { name } }, {
        onSuccess: () => setFolderDialog(null),
      })
    }
  }

  return (
    <aside className="w-60 shrink-0 border-r border-slate-200 bg-white flex flex-col overflow-y-auto">
      {/* Role context */}
      <div className="px-4 pt-4 pb-2">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-2">
          {isSuperAdmin ? 'Superadmin' : isAdmin ? 'Department view' : 'Your role'}
        </p>
        <div className="flex flex-wrap gap-1.5">
          {roleOptions.map((role) => (
            <button
              key={role}
              onClick={() => onSelectDepartment(role)}
              className={cn(
                'px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors inline-flex items-center gap-1',
                department === role
                  ? 'bg-emerald-600 text-white border-emerald-600'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-emerald-300',
                (isSuperAdmin || isAdmin) ? '' : 'cursor-default'
              )}
              title={isSuperAdmin ? `Superadmin department` : isAdmin ? `Filter notes by ${role}` : `Your notes are tagged ${role}`}
            >
              {role}
            </button>
          ))}
        </div>
      </div>

      {/* Navigation */}
      <nav className="px-2 py-2">
        {navItems.map((item) => {
          const active = view === item.key && (item.key !== 'all' || !folderId)
          return (
            <button
              key={item.key}
              onClick={() => onNavigate(item.key)}
              className={cn(
                'w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors mb-0.5',
                active
                  ? 'bg-emerald-50 text-emerald-700'
                  : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
              )}
            >
              <item.icon className={cn('h-4 w-4', active ? 'text-emerald-600' : 'text-slate-400')} />
              <span className="flex-1 text-left">{item.label}</span>
              {item.count !== undefined && item.count > 0 && (
                <span className={cn('text-xs tabular-nums', active ? 'text-emerald-600' : 'text-slate-400')}>{item.count}</span>
              )}
            </button>
          )
        })}
      </nav>

      {/* Folders */}
      <div className="px-2 pb-4 flex-1">
        <div className="flex items-center justify-between px-3 py-2">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Folders</p>
          {canCreateFolders && (
            <button onClick={openCreateFolder} className="p-1 rounded hover:bg-slate-100 text-slate-500" title="New folder">
              <Plus className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        {folders.length === 0 && (
          <p className="px-3 text-xs text-slate-400">
            {canCreateFolders ? 'No folders yet — add one with +' : 'No folders yet'}
          </p>
        )}
        {folders.map((folder) => {
          const active = folderId === folder._id
          return (
            <div
              key={folder._id}
              className={cn(
                'group flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm cursor-pointer transition-colors mb-0.5',
                active ? 'bg-emerald-50 text-emerald-700' : 'text-slate-600 hover:bg-slate-50'
              )}
              onClick={() => onSelectFolder(active ? '' : folder._id)}
            >
              <span className={cn('h-2.5 w-2.5 rounded-sm shrink-0', FOLDER_DOT_COLORS[folder.color || 'green'] || 'bg-emerald-500')} />
              <span className="flex-1 truncate">{folder.name}</span>
              {folder.noteCount > 0 && <span className="text-xs text-slate-400 tabular-nums">{folder.noteCount}</span>}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    onClick={(e) => e.stopPropagation()}
                    className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-slate-100"
                  >
                    <MoreVertical className="h-3.5 w-3.5 text-slate-400" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-36">
                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openRenameFolder(folder) }} className="flex items-center gap-2 text-sm">
                    <Pencil className="h-3.5 w-3.5" /> Rename
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={(e) => { e.stopPropagation(); setDeletingFolder(folder) }}
                    className="flex items-center gap-2 text-sm text-red-600"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )
        })}
      </div>

      {/* Team hint card */}
      <div className="mx-3 mb-4 rounded-xl bg-emerald-50/70 border border-emerald-100 p-3">
        <p className="text-xs font-semibold text-emerald-800 flex items-center gap-1.5">
          <Users className="h-3.5 w-3.5" /> Keep your team aligned
        </p>
        <p className="text-[11px] text-emerald-700/80 mt-1 leading-snug">
          Share important updates and collaborate on notes in real-time.
        </p>
      </div>

      {/* Folder dialogs */}
      <Dialog open={!!folderDialog} onOpenChange={(open) => !open && setFolderDialog(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-base">
              {folderDialog?.mode === 'rename' ? 'Rename folder' : 'New folder'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submitFolderDialog} className="space-y-4 p-1">
            <Input
              autoFocus
              value={folderName}
              onChange={(e) => setFolderName(e.target.value)}
              placeholder="Folder name"
              maxLength={80}
            />
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" size="sm" onClick={() => setFolderDialog(null)}>
                <X className="h-4 w-4 mr-1" /> Cancel
              </Button>
              <Button
                type="submit"
                size="sm"
                disabled={!folderName.trim() || folderMutations.create.isPending || folderMutations.update.isPending}
              >
                <Check className="h-4 w-4 mr-1" />
                {folderDialog?.mode === 'rename' ? 'Save' : 'Create'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deletingFolder} onOpenChange={(open) => !open && setDeletingFolder(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-base">Delete folder?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            "{deletingFolder?.name}" will be deleted. Its notes are kept and moved to unfiled.
          </p>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={() => setDeletingFolder(null)}>Cancel</Button>
            <Button
              size="sm"
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={folderMutations.remove.isPending}
              onClick={() => {
                if (!deletingFolder) return
                folderMutations.remove.mutate(deletingFolder._id, {
                  onSuccess: () => {
                    if (folderId === deletingFolder._id) onSelectFolder('')
                    setDeletingFolder(null)
                  },
                })
              }}
            >
              <Trash2 className="h-4 w-4 mr-1" /> Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </aside>
  )
}
